import React, { useMemo } from "react";
import { Polyline, Popup, useMap } from "react-leaflet";
import Map, { OriginMarker, DestinationMarker, StopMarker } from "../../../../components/molecules/Map/Map";
import { formatearHoraAmPm } from "../../../../utilidades/formateos";

/**
 * Decodifica una polyline almacenada en Base64 que contiene un JSON array
 * de coordenadas [{ lat, lng }].
 *
 * @param {string} base64String - Polyline codificada en base64
 * @returns {Array<[number, number]>} - Array de [lat, lng] para Leaflet
 */
const decodificarPolyline = (base64String) => {
    if (!base64String) return [];
    try {
        const jsonString = atob(base64String);
        const coordenadas = JSON.parse(jsonString).geometry.coordinates;
        if (!Array.isArray(coordenadas)) return [];
        return coordenadas
            .filter((c) => c && typeof c.lat === "number" && typeof c.lng === "number")
            .map((c) => [c.lat, c.lng]);
    } catch (error) {
        console.error("Error al decodificar polyline:", error);
        return [];
    }
};

/**
 * Componente interno que ajusta el mapa para mostrar todos los puntos.
 */
const AjustarVista = ({ puntos }) => {
    const map = useMap();

    React.useEffect(() => {
        if (puntos.length > 0) {
            const bounds = puntos.map((p) =>
                Array.isArray(p) ? p : [p.lat, p.lng || p.lon]
            );
            map.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 });
        }
    }, [puntos, map]);

    return null;
};

/**
 * Muestra el mapa con el trazo de la ruta y sus paradas.
 * Solo lectura: el usuario no puede interactuar con marcadores.
 *
 * @param {Object}   props
 * @param {string}   props.polylineBase64  - Polyline codificada en base64
 * @param {Array}    props.paradas         - Array de paradas con lat, lon, nombre, orden
 * @param {string}   [props.mensajeLetrero] - Mensaje del letrero de la ruta
 */
const MapaComponent = ({ polylineBase64, paradas = [], mensajeLetrero }) => {
    const puntosTrazo = useMemo(
        () => decodificarPolyline(polylineBase64),
        [polylineBase64]
    );

    const paradasOrdenadas = useMemo(
        () => [...paradas].sort((a, b) => a.orden - b.orden),
        [paradas]
    );

    // Sin polyline ni paradas = no hay nada que mostrar
    const tieneDatos = puntosTrazo.length > 0 || paradasOrdenadas.length > 0;

    if (!tieneDatos) {
        return (
            <div className="mapa-trazo__vacio">
                <i className="pi pi-map text-3xl mb-2" style={{ color: "#bbb" }} />
                <p>No hay trazo disponible para mostrar en el mapa.</p>
            </div>
        );
    }

    // Calcular centro y todos los puntos para fitBounds
    const todosLosPuntos = [
        ...puntosTrazo,
        ...paradasOrdenadas.map((p) => [p.lat, p.lon]),
    ];

    const centroInicial = todosLosPuntos.length > 0
        ? todosLosPuntos[0]
        : [20.6597, -103.3496]; // Guadalajara como fallback

    // Determinar inicio y fin del trazo
    const puntoInicio = puntosTrazo.length > 0
        ? { lat: puntosTrazo[0][0], lng: puntosTrazo[0][1] }
        : null;

    const puntoFin = puntosTrazo.length > 1
        ? { lat: puntosTrazo[puntosTrazo.length - 1][0], lng: puntosTrazo[puntosTrazo.length - 1][1] }
        : null;

    return (
        <div className="mapa-trazo">
            {mensajeLetrero && (
                <div className="mapa-trazo__letrero">
                    <i className="pi pi-megaphone mr-2" />
                    Letrero: <strong>{mensajeLetrero}</strong>
                </div>
            )}

            <div className="mapa-trazo__contenedor">
                <Map
                    center={centroInicial}
                    zoom={13}
                    scrollWheelZoom={false}
                    style={{ height: "350px", width: "100%", borderRadius: "8px" }}
                >
                    {/* Ajustar vista a todos los puntos */}
                    <AjustarVista puntos={todosLosPuntos} />

                    {/* Línea del trazo */}
                    {puntosTrazo.length > 1 && (
                        <Polyline
                            positions={puntosTrazo}
                            pathOptions={{
                                color: "#1976D2",
                                weight: 4,
                                opacity: 0.8,
                                dashArray: null,
                            }}
                        />
                    )}

                    {/* Marcador de inicio */}
                    {puntoInicio && (
                        <OriginMarker
                            position={[puntoInicio.lat, puntoInicio.lng]}
                            draggable={false}
                        />
                    )}

                    {/* Marcador de fin */}
                    {puntoFin && (
                        <DestinationMarker
                            position={[puntoFin.lat, puntoFin.lng]}
                            draggable={false}
                        />
                    )}

                    {/* Marcadores de paradas intermedias */}
                    {paradasOrdenadas.map((parada) => (
                        <StopMarker
                            key={parada.id}
                            position={[parada.lat, parada.lon]}
                            draggable={false}
                        >
                            <Popup>
                                <div style={{ minWidth: "150px" }}>
                                    <strong>
                                        Parada {parada.orden}: {parada.nombre}
                                    </strong>
                                    {parada.horario && (
                                        <p style={{ margin: "4px 0 0" }}>
                                            🕐 {formatearHoraAmPm(parada.horario)}
                                        </p>
                                    )}
                                    {parada.referencia && (
                                        <p style={{ margin: "4px 0 0", color: "#666" }}>
                                            📍 {parada.referencia}
                                        </p>
                                    )}
                                </div>
                            </Popup>
                        </StopMarker>
                    ))}
                </Map>
            </div>

            {/* Leyenda del mapa */}
            <div className="mapa-trazo__leyenda">
                <span className="mapa-trazo__leyenda-item">
                    <span className="mapa-trazo__leyenda-color" style={{ backgroundColor: "#00b300" }} />
                    Inicio
                </span>
                <span className="mapa-trazo__leyenda-item">
                    <span className="mapa-trazo__leyenda-color" style={{ backgroundColor: "#0066ff" }} />
                    Paradas
                </span>
                <span className="mapa-trazo__leyenda-item">
                    <span className="mapa-trazo__leyenda-color" style={{ backgroundColor: "#ff0000" }} />
                    Fin
                </span>
                <span className="mapa-trazo__leyenda-item">
                    <span
                        className="mapa-trazo__leyenda-linea"
                        style={{ backgroundColor: "#1976D2" }}
                    />
                    Recorrido
                </span>
            </div>
        </div>
    );
};

export default MapaComponent;
